import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { commonCss } from "../../StyleConfig/CommonCss";
import LoginBg from "../../Images/login/Loginbg2.jpeg";
import PSPLOGO from "../../Images/Nav/PSPLOGO.png";

/**
 * Frosted-glass card shell for the auth pages, letting the background image show through.
 * - title / subtitle render under the brand logo
 * - children is the form
 * - plain: renders only children (used for the in-app embedded reset dialog)
 */
const AuthShell = ({ title, subtitle, children, plain = false, maxWidth = "560px" }) => {
  if (plain) {
    return (
      <Box sx={{ width: "100%" }}>
        <Header title={title} subtitle={subtitle} />
        {children}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        p: 2,
        backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.2), rgba(15, 23, 42, 0.2)), url(${LoginBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Card */}
      <Box sx={{ position: "relative", zIndex: 10, width: "100%", maxWidth }}>
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            background: "rgba(255, 255, 255, 0.5)",
            backdropFilter: "blur(1px)",
            WebkitBackdropFilter: "blur(1px)",
            borderRadius: "1.5rem",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.3)",
            p: { xs: "1.5rem", sm: "2.5rem" },
          }}
        >
          <Header title={title} subtitle={subtitle} />
          {children}
        </Paper>
      </Box>
    </Box>
  );
};

const Header = ({ title, subtitle }) => (
  <Box sx={{ textAlign: "center", mb: "1.75rem" }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.9rem",
        mb: "1rem",
      }}
    >
      <img
        src={PSPLOGO}
        alt="logo"
        style={{
          width: 54,
          height: 54,
          objectFit: "contain",
          filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15))",
        }}
      />
      <Typography
        noWrap
        sx={{
          fontSize: "1.6rem",
          fontWeight: 700,
          letterSpacing: "0.5px",
          color: commonCss.blue,
          textShadow: "0 1px 2px rgba(255, 255, 255, 0.5)",
        }}
      >
        e-Permit to Work
      </Typography>
    </Box>
    {title && (
      <Typography
        sx={{
          fontSize: "1.6rem",
          fontWeight: 800,
          color: "#1f4374",
          letterSpacing: "-0.02em",
          textShadow: "0 1px 2px rgba(255, 255, 255, 0.5)",
        }}
      >
        {title}
      </Typography>
    )}
    {subtitle && (
      <Typography
        sx={{
          color: "#334155",
          fontSize: "0.92rem",
          fontWeight: 600,
          mt: 0.5,
          textShadow: "0 1px 2px rgba(255, 255, 255, 0.5)",
        }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

export const authFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: commonCss.inputBackgroundColor,
    borderRadius: commonCss.inputBorderRadius,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: commonCss.inputBorderColor,
  },
};

export const authButtonSx = (isLoading) => ({
  py: "0.6rem",
  mt: "0.5rem",
  color: commonCss.buttonTextColor,
  // fontWeight: 600,
  borderRadius: commonCss.buttonBorderRadius,
  textTransform: "none",
  fontSize: "0.9rem",
  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.15)",
  background: isLoading ? "default" : commonCss.blue,
  "&:hover": {
    backgroundColor: isLoading ? "default" : commonCss.darkBlue,
  },
  "&.Mui-disabled": { opacity: 0.6, color: commonCss.buttonTextColor },
});

export const authLabelSx = {
  fontWeight: commonCss.labelFontWeight,
  color: commonCss.labelInputColor,
};

export default AuthShell;
